import { errorName } from "../types/data";

const DB_NAME = "advanced-movie-rating-cache";
const DB_VERSION = 1;
const STORE_NAME = "entries";

let databasePromise: Promise<IDBDatabase> | undefined;

const openDatabase = () => {
  if (databasePromise) return databasePromise;

  databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => {
        database.close();
        databasePromise = undefined;
      };
      resolve(database);
    };
    request.onerror = () => reject(request.error);
  }).catch((error: unknown) => {
    // A transient quota/private-mode/open failure must not poison every future
    // cache operation for the whole lifetime of the tab.
    databasePromise = undefined;
    throw error;
  });

  return databasePromise;
};

const runTransaction = async <T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const database = await openDatabase();

  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);
      const request = operation(store);
      let requestResult: T;
      let settled = false;

      const rejectOnce = (error: DOMException|null) => {
        if (settled) return;
        settled = true;
        reject(error);
      };

      // A successful request is not the same as a committed transaction.
      // Waiting for `oncomplete` prevents an immediate invalidate/read from
      // observing the value that the previous transaction was meant to
      // replace or delete.
      request.onsuccess = () => {
        requestResult = request.result;
      };
      request.onerror = () => rejectOnce(request.error);
      transaction.oncomplete = () => {
        if (settled) return;
        settled = true;
        resolve(requestResult);
      };
      transaction.onerror = () => rejectOnce(transaction.error);
      transaction.onabort = () => rejectOnce(transaction.error);
    });
  } catch (error) {
    if (errorName(error) === "InvalidStateError" || errorName(error) === "NotFoundError") {
      databasePromise = undefined;
    }
    throw error;
  }
};

export const readIndexedDbValue = <T = unknown>(key: IDBValidKey): Promise<T | undefined> =>
  runTransaction<T | undefined>("readonly", (store) => store.get(key));

export const writeIndexedDbValue = (key: IDBValidKey, value: unknown) =>
  runTransaction("readwrite", (store) => store.put(value, key));

export const deleteIndexedDbValue = (key: IDBValidKey) =>
  runTransaction("readwrite", (store) => store.delete(key));
